import React, { useEffect, useState } from 'react';
import { useAlert, useAuth } from 'core/contexts';
import { commonShowAlert } from '../../../../lib';
import {
    LifeGraphData,
    LifeGraphEdge,
    LifegraphExplorerProps,
    LifeGraphNode,
    LifeGraphVertice,
    Link,
    MappableEdges,
    MappableVertices,
    NeighborLink,
    NormalizedLifeGraphData,
} from './lifegraph-explorer.types';
import { HelixLoader } from 'components';
import { Graph2D, Graph3D } from '../graphs';
import { NodeDetailDrawer } from '../node-detail-drawer';
import { useGraphChain } from 'hooks';
import { AxiosResponse } from 'axios';
import { LifegraphEmptyStateError } from './LifegraphEmptyStateError';
import { UnsupportedLGQuery } from './UnsupportedLGQuery';

export const LifeGraphExplorer = (
    props: LifegraphExplorerProps
): React.JSX.Element => {
    const {
        filter,
        nodeColors,
        displayMode,
        canvasHeight,
        canvasWidth,
        onLoadingChange,
        dictionaries = [],
        forceUpdate,
        setForceUpdate,
        isCanvasHeightSmall,
        unsupportedQuery,
        handleUnsupportedQueryClick,
    } = props;
    const { getToken } = useAuth();
    const { showAlert, hideAlert } = useAlert();
    const [currentQueryString, setCurrentQueryString] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [rawGraphData, setRawGraphData] = useState<any>();
    const [selectedNode, setSelectedNode] = useState(undefined);
    const [graphData, setGraphData] = useState<
        NormalizedLifeGraphData | undefined
    >(undefined);
    const { mutate: graphChain, isLoading, isError } = useGraphChain();

    useEffect(() => {
        const state = !!graphData?.nodes.length && !isLoading && !isError;
        onLoadingChange(state);
    }, [isLoading, isError, graphData]); // eslint-disable-line react-hooks/exhaustive-deps

    const getGraphChain = (newFilter: any): void => {
        graphChain(
            {
                filter: newFilter,
                token: getToken(),
            },
            {
                onSuccess: handleSuccess, // eslint-disable-line @typescript-eslint/no-use-before-define
            }
        );
    };

    const findNeighbors = (
        node: LifeGraphNode,
        links: NeighborLink[] = []
    ): Link[] => {
        const key = node.key;
        return links.filter(
            (link) => link.source === key || link.target === key
        );
    };

    const filterUnconnectedLinks = (
        vertices: LifeGraphVertice[],
        links: NeighborLink[]
    ): NeighborLink[] => {
        const connectedLinks: NeighborLink[] = [];

        /**
         * Verify that each link has an existing target node (vertice). If the target node doesn't exist, then the current
         * link cannot be connected or called a neighbor link. Hence, we don't want to remove this link connection.
         */
        for (const vertice of vertices) {
            for (const link of links) {
                if (
                    vertice.header.id === link.target &&
                    vertices.some((item) => item.header.id === link.source)
                )
                    connectedLinks.push(link);
            }
        }

        return connectedLinks;
    };

    const normalizeGraphData = (
        normalizedData: NormalizedLifeGraphData,
        edges: LifeGraphEdge[],
        vertices: LifeGraphVertice[]
    ): NormalizedLifeGraphData => {
        let normalizedDataCopy = { ...normalizedData };

        const links: NeighborLink[] = edges.map((edge: LifeGraphEdge) => ({
            label: edge.label,
            source: edge.fromSDOId,
            target: edge.toSDOId,
            linknum: 0,
            edge,
        }));

        // filter/remove any links whose target nodes don't exist.
        const connectedLinks: NeighborLink[] = filterUnconnectedLinks(
            vertices,
            links
        );

        /**
         * For the multiple edges to display on LifeGraph, we need to make sure the links are sorted by source and
         * target, and we increase the linknum for links with duplicated source and targets.
         *
         * The current linknum will be used by linkSettings to draw the link based on its linkCurvature. The first
         * link will always be a straight line while the rest will be rendered with a curve.
         *
         * Example taken from: https://stackoverflow.com/questions/37417459/drawing-multiple-links-between-fixed-nodes
         */

        //sort links by source, then target
        connectedLinks.sort(function (a, b) {
            if (a.source > b.source) {
                return 1;
            } else if (a.source < b.source) {
                return -1;
            } else {
                if (a.target > b.target) {
                    return 1;
                }
                if (a.target < b.target) {
                    return -1;
                } else {
                    return 0;
                }
            }
        });

        //any links with duplicate source and target get an incremented 'linknum'
        for (let i = 0; i < connectedLinks.length; i++) {
            if (
                i != 0 &&
                connectedLinks[i].source == connectedLinks[i - 1].source &&
                connectedLinks[i].target == connectedLinks[i - 1].target
            ) {
                connectedLinks[i].linknum = connectedLinks[i - 1].linknum + 1;
            } else {
                connectedLinks[i].linknum = 0;
            }
        }

        const normalizedNodes: LifeGraphNode[] = vertices.map(
            (vertice: LifeGraphVertice) => ({
                key: vertice.header.id,
                sdo: {
                    data: vertice.data,
                    header: vertice.header,
                    metadata: vertice.metadata,
                    verifications: vertice.verifications,
                },
            })
        );

        for (const node of normalizedNodes) {
            node.neighbors = findNeighbors(node, connectedLinks);
        }

        // Add the links and nodes to the normalized object to display each unconnected graph.
        normalizedDataCopy.links.push(...connectedLinks);
        normalizedDataCopy.nodes.push(...normalizedNodes);

        return normalizedDataCopy;
    };

    const normalizeLifeGraph = (
        lifeGraphData: LifeGraphData[] = []
    ): NormalizedLifeGraphData => {
        let normalizedData: NormalizedLifeGraphData = { links: [], nodes: [] };

        if (!lifeGraphData.length) {
            return normalizedData;
        }

        // Get the edges and vertices for the only item returned by the API.
        const responseDataItem = lifeGraphData[0];

        /**
         * Determine what type of map response we are getting from the server:
         *
         * 1. If the vertices and edges are an array, we are getting the legacy (default) graph type.
         * 2. If the vertices and edges are a map (document), we are now getting the new Mappable graph type.
         */
        const isNonMappableGraph =
            Array.isArray(responseDataItem.edges) &&
            Array.isArray(responseDataItem.vertices);

        if (isNonMappableGraph) {
            const edges: LifeGraphEdge[] =
                (responseDataItem?.edges as LifeGraphEdge[]) ?? [];
            const vertices: LifeGraphVertice[] =
                (responseDataItem?.vertices as LifeGraphVertice[]) ?? [];
            normalizedData = normalizeGraphData(
                normalizedData,
                edges,
                vertices
            );
        } else {
            // normalize the objects into a list so we can use the same code as per the original structure.
            const mappableEdges: MappableEdges =
                (responseDataItem?.edges as MappableEdges) ?? {};
            const mappableVertices: MappableVertices =
                (responseDataItem?.vertices as MappableVertices) ?? {};
            normalizedData = normalizeGraphData(
                normalizedData,
                Object.values(mappableEdges),
                Object.values(mappableVertices)
            );
        }

        return normalizedData;
    };

    const updateLifeGraph = (data: any): void => {
        const normalizedData = normalizeLifeGraph(data);
        setGraphData(normalizedData);
    };

    const handleFilterChange = (newQueryString: string): void => {
        // if the new query string is not the same as the current, refetch data from server with new query.
        if (newQueryString === currentQueryString) {
            // if the new query string is the same as the current, reset graphData with the current rawGraphData.
            updateLifeGraph(rawGraphData);
        } else if (
            newQueryString !== currentQueryString &&
            newQueryString.toLowerCase().includes('data_only')
        ) {
            // This is short-term fix to prevent the UI from blowing up until
            // we get the list of all unsupported graph types and graph options.

            setRawGraphData(undefined);
            updateLifeGraph(undefined);
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'DATA_ONLY is not supported'
            );
        } else {
            getGraphChain(newQueryString);
            setCurrentQueryString(newQueryString);
        }
    };

    useEffect(() => {
        if (forceUpdate) {
            updateLifeGraph(rawGraphData);
            setForceUpdate(false);
            return;
        }
        // do not fetch the current query if it's unsupported and display the unsupported view instead.
        if (unsupportedQuery) return;

        // if the filter is a string then we are calling it from advanced filter mode.
        if (typeof filter === 'string') {
            handleFilterChange(filter);
        }
        // if the filter is an object, then we are calling it from "basic" mode. Hence, convert the filter into a query string for comparison.
        if (typeof filter === 'object') {
            const newQueryString = `GRAPH ${filter.chain} ${
                filter.attributeKey &&
                filter.attributeValue &&
                filter.attributeKey !== 'None'
                    ? `WHERE data.${filter.attributeKey} == ${filter.attributeValue}`
                    : ''
            } LIMIT ${filter.limit} TRAVERSE ANY 0..${filter.depth}`;

            handleFilterChange(newQueryString);
        }
    }, [filter, forceUpdate, unsupportedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleResizeEvent = (): void => {
            globalThis.dispatchEvent(new Event('resize'));
        };

        if (graphData) {
            setTimeout(() => {
                handleResizeEvent();
            }, 500);
        }
    }, [graphData]);

    const handleSuccess = (result: AxiosResponse<any>): void => {
        const rawData: any = result?.data;
        setRawGraphData(rawData);
        updateLifeGraph(rawData);
    };

    const handleDrawerOpen = (open: boolean): void => {
        if (!open) setSelectedNode(undefined);
        setDrawerOpen(open);
    };

    const onUnsupportedQueryClicked = (): void => {
        handleUnsupportedQueryClick?.();
    };

    const renderLifeGraphView = (): React.JSX.Element => {
        if (
            !unsupportedQuery &&
            graphData?.nodes.length &&
            displayMode === '3D'
        ) {
            return (
                <Graph3D
                    graphData={graphData}
                    height={canvasHeight}
                    width={canvasWidth}
                    setDrawerOpen={setDrawerOpen}
                    setSelectedNode={setSelectedNode}
                    nodeColors={nodeColors}
                    dictionaries={dictionaries}
                />
            );
        } else if (
            !unsupportedQuery &&
            graphData?.nodes.length &&
            displayMode === '2D'
        ) {
            return (
                <Graph2D
                    graphData={graphData}
                    height={canvasHeight}
                    width={canvasWidth}
                    setDrawerOpen={setDrawerOpen}
                    setSelectedNode={setSelectedNode}
                    nodeColors={nodeColors}
                    dictionaries={dictionaries}
                />
            );
        } else if (!unsupportedQuery && !graphData?.nodes.length) {
            return (
                <LifegraphEmptyStateError
                    isCanvasHeightSmall={isCanvasHeightSmall}
                />
            );
        } else {
            return (
                <UnsupportedLGQuery
                    isCanvasHeightSmall={isCanvasHeightSmall}
                    handleClick={onUnsupportedQueryClicked}
                />
            );
        }
    };

    if (isLoading) {
        return <HelixLoader />;
    }

    if (isError) {
        return (
            <LifegraphEmptyStateError
                isCanvasHeightSmall={isCanvasHeightSmall}
            />
        );
    }

    return (
        <>
            {renderLifeGraphView()}
            {drawerOpen && selectedNode && (
                <NodeDetailDrawer
                    isOpen={drawerOpen}
                    setDrawerOpen={handleDrawerOpen}
                    data={selectedNode}
                    nodeColors={nodeColors}
                />
            )}
        </>
    );
};
